/**
 * FrozenMidCreateView — a session stopped before its create ever committed a
 * conversationId (U5). Deliberately NOT `PreChatSurface` — that surface's
 * example-question empty state is for a fresh, never-touched draft, not a
 * frozen "here's what you tried to send" state. No `@assistant-ui/react`
 * import here, matching `FlowChatPanel.tsx`'s own eager-chunk membership.
 *
 * Extracted out of `FlowChatPanel.tsx` into its own leaf file (bead
 * terra-design-v2-chat-yub.5) so `FlowChatPanelV2.tsx` can reuse it
 * verbatim for the same frozen-mid-create body branch WITHOUT creating an
 * import cycle — `FlowChatPanel.tsx` itself mounts `FlowChatPanelV2`, so `FlowChatPanelV2` importing FROM
 * `FlowChatPanel.tsx` would cycle back. Both files import this leaf
 * instead. This bead's own scope lists the component by its V1 name, not a
 * `FrozenMidCreateViewV2` restyle — same reuse pattern as
 * `AccountNotFoundState`.
 */
import { useCallback, useState } from 'react';
import { Composer } from '../../skills/Composer';

export interface FrozenMidCreateViewProps {
  pendingFirstMessage: string | null;
  onResume: (text: string) => void;
}

export function FrozenMidCreateView({ pendingFirstMessage, onResume }: FrozenMidCreateViewProps) {
  const [value, setValue] = useState('');
  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      onResume(trimmed);
      setValue('');
    },
    [onResume],
  );

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 mb-2">
          You asked
        </div>
        <div className="rounded-lg border border-secondary dark:border-nightshade-700 px-3 py-2 text-[13px] text-content-primary dark:text-nightshade-100">
          {pendingFirstMessage}
        </div>
      </div>
      <Composer
        value={value}
        onChange={setValue}
        onSend={handleSend}
        personas={[]}
        skills={[]}
        persona={null}
        started={false}
        onPickPersona={() => {}}
        onPickSkill={() => {}}
      />
    </div>
  );
}
